import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { useCreate, useUpdate } from "@refinedev/core";
import { ConfirmModal } from "components/confirmModal";
import { DropdownMenu } from "components/dropdownMenu";
import { ISimpleEventDetail, IUsuallyTypeTicketForm, ShowTime } from "interfaces";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { ShowTimeModal } from "./showTimeModal";
import { HistoryLodModal } from "components/historyLogModal";

interface ShowTimesProps {
  eventDetailData: ISimpleEventDetail;
  fetchShowTimes: () => void;
}

interface ShowTimeField extends ShowTime {
  field_id: string;
}

const INITIAL_SHOWTIME_CONFIG = {
  open: false,
  defaultValue: "",
  submit: (data: any) => {},
};

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {}
}

export const Showtimes = ({eventDetailData, fetchShowTimes}: ShowTimesProps) => {
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);

  const [showTimeModalConfig, setShowTimeModalConfig] = useState(INITIAL_SHOWTIME_CONFIG);
  const [activeShowTimeStatusModalConfig, setActiveShowTimeStatusModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const { mutate: createMutation, isLoading: createMutationLoading } = useCreate();
  const { mutate: updateMutation } = useUpdate();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IUsuallyTypeTicketForm>(); // retrieve all hook methods

  const { fields, append } = useFieldArray({
    control,
    name: "showtimes",
    keyName: "field_id"
  });

  const _addShowTime = (data: any) => {
    createMutation(
      {
        resource: "content/events",
        meta: {
          URLSuffix: `attractions/${eventDetailData.id ?? ""}/showtimes`,
          ignoreResourceWrapper: true,
        },
        values: {
          showtimes: [data],
        },
      },
      {
        onSuccess: (data) => {
          fetchShowTimes();
          toast.success("Added successfully");
        },
      }
    );
  };

  const _updateShowTime = (data: ShowTime, index: number) => {
    updateMutation(
      {
        resource: "content/events",
        id: `attractions/${eventDetailData.id ?? ""}/showtimes`,
        meta: {
          ignoreResourceWrapper: true
        },
        values: {
          showtimes: [data],
        },
      },
      {
        onSuccess: (data) => {
          toast.success("Updated successfully");
        },
      }
    );
  };


  const _onCloseModal = () => {
    setShowTimeModalConfig({
      ...showTimeModalConfig,
      open: false,
      submit: (data: any) => {},
    });
  }

  const _onCloseActiveShowTimeModal = () => {
    setActiveShowTimeStatusModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      title: activeShowTimeStatusModalConfig.title
    });
  }

  const showtimesData = watch("showtimes");

  return (
    <div className="box-container p-4">
      <div>
        <div className="grid gap-y-4 sm:grid-cols-6">
          {fields.map((showtime: ShowTimeField, index: number) => (
            <div 
              className="col-span-full flex flex-row flex-wrap gap-y-2  items-start justify-between rounded-md border border-gray-300 px-4 py-2.5" 
              key={showtime.field_id}
            >
              <div className="flex flex-row flex-wrap shrink-0">
                <p>{get(showtimesData[index], "time", "")}</p>
              </div>
              <div className="flex flex-row items-center self-end sm:self-start gap-x-2">
                {showtimesData[index].is_active ? (
                  <span
                    className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-green-100 ring-green-100 text-green-700`}
                  >
                    Active
                  </span>
                ) : (
                  <span
                    className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-neutral-800 ring-magnolia-600`}
                  >
                    Inactive
                  </span>
                )}
                {showtimesData[index].is_active ? showtimesData[index].hide_in_ui ? (
                  <span
                    className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-neutral-800 ring-magnolia-600`}
                  >
                    Hide in PROD
                  </span>
                ) : (
                  <span
                    className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-green-100 ring-green-100 text-green-700`}
                  >
                    Show in PROD
                  </span>
                ) : <></>}
                <DropdownMenu
                  label={
                    <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                  }
                  items={[
                    ...(showtimesData[index].is_active
                    ? [
                        {
                          label: "Edit",
                          onClick: () => setShowTimeModalConfig({
                            open: true,
                            defaultValue: watch("showtimes")[index].time,
                            submit: (data) => {
                              _updateShowTime({
                                ...watch("showtimes")[index],
                                time: data,
                              }, index);
                            }
                          }),
                        },
                      ]
                    : []),
                    {
                      label: showtimesData[index].is_active
                        ? "Deactivate"
                        : "Activate",
                        onClick: () => setActiveShowTimeStatusModalConfig({
                          open: true,
                          title: `Are you sure you want to ${showtimesData[index].is_active ? "deactivate" : "activate"} this showtime?`,
                          submit: () => {
                            _updateShowTime({
                              ...watch("showtimes")[index],
                              is_active: showtimesData[index].is_active ? false : true,
                            }, index);
                          }
                        })
                      },
                      ...(showtimesData[index].is_active
                      ? [
                          {
                            label: showtimesData[index].hide_in_ui
                            ? "Show in PROD"
                            : "Hide in PROD",
                            onClick: () => setActiveShowTimeStatusModalConfig({
                              open: true,
                              title: `Are you sure you want to ${showtimesData[index].hide_in_ui ? "show" : "hide"} hide this showtime in PROD?`,
                              submit: () => {
                                _updateShowTime({
                                  ...watch("showtimes")[index],
                                  hide_in_ui: showtimesData[index].hide_in_ui ? false : true
                                }, index)
                              },
                            })
                          },
                        ]
                      : []),
                      {
                        label: "Log",
                        onClick: () => setOpenLogModalId(showtimesData[index].id ?? "")
                      }
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="text-center text-sm">
          {isEmpty(fields) ? <p>No Showtime</p> : <></>}
        </div>
      </div>
      <div className="add_edit_showtime">
        <div className="w-full text-center">
          <button
            type="button"
            className="flex flex-row items-center mx-auto mt-4 w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
            onClick={() => setShowTimeModalConfig({
              open: true,
              defaultValue: "",
              submit: (data) => _addShowTime({
                time: data,
                hide_in_ui: false,
                is_active: true,
              })
            })}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
            <span className="shrink-0 text-sm">Add new showtime</span>
          </button>
        </div>
        <ConfirmModal 
          key="active-showtime"
          title={activeShowTimeStatusModalConfig.title}
          content=""
          open={activeShowTimeStatusModalConfig.open}
          onConfirm={activeShowTimeStatusModalConfig.submit}
          onClose={_onCloseActiveShowTimeModal}
        />
        <ShowTimeModal 
          open={showTimeModalConfig.open}
          onClose={_onCloseModal}
          defaultValue={showTimeModalConfig.defaultValue}
          onSubmit={showTimeModalConfig.submit}
        />
        <HistoryLodModal
          resource={`content/events/attractions/${get(eventDetailData, "id", "")}/showtimes/${openLogModalId}/history`}
          open={openLogModalId ? true : false}
          onClose={() => setOpenLogModalId(null)}
          key="showtimes"
          id="showtimes"
        />
      </div>
    </div>
  );
}